import {
  toGroupedFilters,
  type EmailRule,
  type EmailRuleConditionField,
  type EmailRuleConditionGroup,
  type EmailRuleFilters,
} from '@erp/rpc';

const FIELD_LABELS: Record<EmailRuleConditionField, string> = {
  from: 'From',
  to: 'To',
  cc: 'CC',
  bcc: 'BCC',
  subject: 'Subject',
  mailbox: 'Mailbox',
};

const ATTACHMENT_LINE = /^(Has attachments|No attachments)$/;

/**
 * Renders one group of conditions as a single readable line, e.g.
 * `To or Cc contains "aurora@"`. Conditions are joined by the group's
 * operator word ("or"/"and"). Each condition reads as
 * `<Field> <contains|does not contain> "<value>"`, but when consecutive
 * conditions share a match type the field labels collapse so a "To OR Cc"
 * group reads `To or Cc contains "x"`.
 */
function summarizeGroup(group: EmailRuleConditionGroup): string | null {
  const conditions = group.conditions
    .map((c) => ({ ...c, value: c.value.trim() }))
    .filter((c) => c.value.length > 0);
  if (conditions.length === 0) return null;

  const joiner = group.operator === 'or' ? ' or ' : ' and ';

  const parts = conditions.map((c) => {
    const verb =
      c.matchType === 'notContains' ? 'does not contain' : 'contains';
    return `${FIELD_LABELS[c.field]} ${verb} "${c.value}"`;
  });

  return parts.join(joiner);
}

/**
 * Human-readable lines describing an email rule's match criteria. Filters are
 * normalized to the grouped shape first, so legacy and grouped rules render
 * identically — one line per group.
 */
export function summarizeEmailRuleFiltersAsLines(
  filters: EmailRuleFilters | undefined | null,
): string[] {
  if (!filters) return [];

  const grouped = toGroupedFilters(filters);
  const lines: string[] = [];

  // Groups combine via the top-level operator (a two-level boolean tree), so
  // prefix each group line after the first with "and"/"or" to keep multi-group
  // previews accurate. A single group needs no prefix.
  const groupJoiner = grouped.operator === 'or' ? 'or ' : 'and ';
  const groupLines: string[] = [];
  for (const group of grouped.groups) {
    const line = summarizeGroup(group);
    if (line) groupLines.push(line);
  }
  groupLines.forEach((line, i) => {
    lines.push(i === 0 ? line : `${groupJoiner}${line}`);
  });

  if (grouped.hasAttachments === true) {
    lines.push('Has attachments');
  } else if (grouped.hasAttachments === false) {
    lines.push('No attachments');
  }

  return lines;
}

/** Lines that restrict who/what mailbox/subject (vs attachment-only flags). */
export function partitionEmailRuleFilterLines(filterLines: string[]): {
  addressLines: string[];
  attachmentLines: string[];
} {
  const addressLines = filterLines.filter((l) => !ATTACHMENT_LINE.test(l));
  const attachmentLines = filterLines.filter((l) => ATTACHMENT_LINE.test(l));
  return { addressLines, attachmentLines };
}

function slugForCompare(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/** True when trigger / enum label adds no information beyond the rule title. */
export function isEmailRuleEventNameRedundantWithRule(
  ruleName: string,
  eventName: string | null | undefined,
  coreOptionName: string | null | undefined,
): boolean {
  const r = slugForCompare(ruleName);
  if (!r) return false;
  for (const raw of [eventName, coreOptionName]) {
    if (!raw) continue;
    const e = slugForCompare(raw);
    if (!e) continue;
    if (r === e || r.includes(e) || e.includes(r)) return true;
  }
  return false;
}

export type EmailRuleEventLookupEntry = {
  ruleId: string;
  ruleName: string;
  eventName: string | null;
  eventDescription: string | null;
  filterLines: string[];
};

/** Map email rule *event* option id → parent rule + event + filter summary */
export function buildEmailRuleEventLookup(
  rules: EmailRule[] | undefined,
): Map<string, EmailRuleEventLookupEntry> {
  const map = new Map<string, EmailRuleEventLookupEntry>();
  if (!rules) return map;

  for (const rule of rules) {
    const filterLines = summarizeEmailRuleFiltersAsLines(rule.filters);
    for (const ev of rule.events ?? []) {
      map.set(ev.id, {
        ruleId: rule.id,
        ruleName: rule.ruleName,
        eventName: ev.name,
        eventDescription: ev.description,
        filterLines,
      });
    }
  }
  return map;
}
